import React, { useState, useEffect } from 'react';
import { CreditCard, Wallet, Loader2, CheckCircle, XCircle, Clock, ExternalLink } from 'lucide-react';
import api from '../../api/axios';
import { Button } from '../ui/button';
import { toast } from 'sonner';

export const MercadoPagoButton = ({ 
  type = 'credits', // 'credits' or 'products'
  quantity = 1, 
  items = [], 
  onSuccess,
  className = '' 
}) => {
  const [loading, setLoading] = useState(false);
  const [mpEnabled, setMpEnabled] = useState(false);
  const [checkingConfig, setCheckingConfig] = useState(true);

  useEffect(() => {
    checkMPConfig();
  }, []);

  const checkMPConfig = async () => {
    try {
      const response = await api.get('/payments/config');
      setMpEnabled(response.data.enabled);
    } catch (error) {
      setMpEnabled(false);
    } finally {
      setCheckingConfig(false);
    }
  };

  const handlePayment = async () => {
    setLoading(true);
    try {
      const paymentData = type === 'credits' 
        ? { type: 'credits', quantity }
        : { type: 'products', items };

      const response = await api.post('/payments/create-preference', paymentData);
      
      // Redirigir a MercadoPago
      if (response.data.init_point) {
        window.location.href = response.data.init_point;
      } else {
        toast.error('Error al iniciar el pago');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al procesar el pago');
    } finally {
      setLoading(false);
    }
  };

  if (checkingConfig) {
    return (
      <Button disabled className={className}>
        <Loader2 className="h-4 w-4 animate-spin mr-2" />
        Cargando...
      </Button>
    );
  }

  if (!mpEnabled) {
    return null; // No mostrar si MP no está habilitado
  }

  const total = type === 'credits' 
    ? quantity * 35000 
    : items.reduce((sum, item) => sum + (item.total || 0), 0);

  return (
    <Button
      onClick={handlePayment}
      disabled={loading}
      className={`bg-[#009EE3] hover:bg-[#007EB5] text-white ${className}`}
    >
      {loading ? (
        <Loader2 className="h-4 w-4 animate-spin mr-2" />
      ) : (
        <CreditCard className="h-4 w-4 mr-2" />
      )}
      Pagar con MercadoPago ${total.toLocaleString('es-AR')}
    </Button>
  );
};

// Payment Result Pages
export const PaymentSuccess = () => {
  const [checking, setChecking] = useState(true);
  const [paymentStatus, setPaymentStatus] = useState(null);

  useEffect(() => {
    // Obtener referencia de la URL
    const params = new URLSearchParams(window.location.search);
    const externalRef = params.get('external_reference');
    
    if (externalRef) {
      checkPaymentStatus(externalRef);
    } else {
      setChecking(false);
    }
  }, []);

  const checkPaymentStatus = async (ref) => {
    try {
      const response = await api.get(`/payments/status/${ref}`);
      setPaymentStatus(response.data);
    } catch (error) {
      console.error('Error checking payment status');
    } finally {
      setChecking(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center py-20 px-4" data-testid="payment-success">
      <div className="max-w-md w-full text-center">
        <div className="w-20 h-20 bg-green-500/20 rounded-full mx-auto flex items-center justify-center mb-6">
          <CheckCircle className="h-10 w-10 text-green-500" />
        </div>
        
        <h1 className="text-3xl font-heading font-bold uppercase mb-2">¡Pago Exitoso!</h1>
        <p className="text-muted-foreground mb-6">
          Tu pago ha sido procesado correctamente.
        </p>

        {checking ? (
          <div className="flex items-center justify-center gap-2 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Verificando pago...
          </div>
        ) : paymentStatus ? (
          <div className="bg-white/5 rounded-xl p-4 mb-6 text-left">
            <div className="flex justify-between mb-2">
              <span className="text-muted-foreground">Estado:</span>
              <span className="text-green-400 font-medium capitalize">{paymentStatus.status}</span>
            </div>
            <div className="flex justify-between mb-2">
              <span className="text-muted-foreground">Monto:</span>
              <span className="font-bold">${paymentStatus.amount?.toLocaleString('es-AR')}</span>
            </div>
            {paymentStatus.credits_granted > 0 && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">Créditos acreditados:</span>
                <span className="font-bold text-primary">{paymentStatus.credits_granted}</span>
              </div>
            )}
          </div>
        ) : null}

        <div className="flex flex-col gap-3">
          <Button asChild className="bg-primary">
            <a href="/dashboard">Ir al Dashboard</a>
          </Button>
          <Button asChild variant="outline">
            <a href="/booking">Reservar Turno</a>
          </Button>
        </div>
      </div>
    </div>
  );
};

export const PaymentFailure = () => {
  return (
    <div className="min-h-screen flex items-center justify-center py-20 px-4" data-testid="payment-failure">
      <div className="max-w-md w-full text-center">
        <div className="w-20 h-20 bg-red-500/20 rounded-full mx-auto flex items-center justify-center mb-6">
          <XCircle className="h-10 w-10 text-red-500" />
        </div>
        
        <h1 className="text-3xl font-heading font-bold uppercase mb-2">Pago Rechazado</h1>
        <p className="text-muted-foreground mb-6">
          No pudimos procesar tu pago. Por favor, intentá nuevamente.
        </p>

        <div className="flex flex-col gap-3">
          <Button asChild className="bg-primary">
            <a href="/booking">Intentar Nuevamente</a>
          </Button>
          <Button asChild variant="outline">
            <a href="/dashboard">Volver al Dashboard</a>
          </Button>
        </div>
      </div>
    </div>
  );
};

export const PaymentPending = () => {
  return (
    <div className="min-h-screen flex items-center justify-center py-20 px-4" data-testid="payment-pending">
      <div className="max-w-md w-full text-center">
        <div className="w-20 h-20 bg-yellow-500/20 rounded-full mx-auto flex items-center justify-center mb-6">
          <Clock className="h-10 w-10 text-yellow-500" />
        </div>
        
        <h1 className="text-3xl font-heading font-bold uppercase mb-2">Pago Pendiente</h1>
        <p className="text-muted-foreground mb-6">
          Tu pago está siendo procesado. Te notificaremos cuando se acredite.
        </p>

        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4 mb-6">
          <p className="text-sm text-yellow-300">
            Los pagos con algunos medios pueden demorar hasta 48 horas en acreditarse.
          </p>
        </div>

        <div className="flex flex-col gap-3">
          <Button asChild className="bg-primary">
            <a href="/dashboard">Ir al Dashboard</a>
          </Button>
        </div>
      </div>
    </div>
  );
};

export default MercadoPagoButton;
